import { AfterViewInit, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { WINDOW } from '@ng-web-apis/common';
import { AuthState, User } from '@fitscovery/auth/data-access';
import { Membership, MembershipState, MembershipStatus, OpenMembershipDetailsModal } from '@fitscovery/membership/data-access';
// import { ModalMembershipComponent } from '@fitscovery/partner-web/shared/modals/membership';
import { Partner, PartnerState, PartnerMembershipSubscriptionState, PartnerMembershipSubscription, GetPartnerActiveMembershipSubscriptionsByPartnerId } from '@fitscovery/partner/data-access';
import { LOCAL_STORAGE } from '@ng-web-apis/common';
import { Select, Store } from '@ngxs/store';
import { forkJoin, Observable } from 'rxjs';
import { delay, filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { JwtHelperService } from '@auth0/angular-jwt';
import { SubscriptionStore } from '@fitscovery/common/classes/subscription-store';

@Component({
  selector: 'fitscovery-ui-be-a-member-button',
  templateUrl: './ui-be-a-member-button.component.html',
  styleUrls: ['./ui-be-a-member-button.component.scss']
})
export class UiBeAMemberButtonComponent extends SubscriptionStore implements OnInit, AfterViewInit, OnDestroy {
  @Input() email: string;
  @Input() memberships?: Membership[];
  @Input() isWithinPage: boolean;
  @Input() isTicketMembershipsOnly: boolean;
  @Input() isWithinUnlockPage: boolean;
  @Output() applyForMembershipClicked: EventEmitter<any>;
  @Output() membershipButtonClicked: EventEmitter<any>;

  hasMembership: boolean;
  membershipStatus: string;
  membershipStatusDisplay: string;
  membershipStatusType = MembershipStatus;

  @Select(AuthState.user) userAuthState$: Observable<User>;
  @Select(PartnerState.partner) partner$: Observable<Partner>;
  @Select(MembershipState.memberships) memberships$: Observable<Membership[]>;
  @Select(MembershipState.hasActiveStatus) hasActiveStatus$: Observable<boolean>;
  @Select(MembershipState.hasActiveFreeSubscriptionStatus) hasActiveFreeSubscriptionStatus$: Observable<boolean>;
  @Select(PartnerMembershipSubscriptionState.subscriptions) activePartnerSubscriptions$: Observable<PartnerMembershipSubscription[]>;

  @Select(MembershipState.hasPendingStatus) hasPendingStatus$: Observable<boolean>;
  @Select(MembershipState.isProcessing) membershipProcessing$: Observable<boolean>;
  @Select(MembershipState.isMembershipButtonClicked) membershipButtonClicked$: Observable<boolean>;
  @Select(MembershipState.loaded) membershipLoaded$: Observable<boolean>;
  @Select(MembershipState.hasAppliedForFreeMembershipWithNoResponse) hasAppliedForFreeMembershipWithNoResponse$: Observable<boolean>;

  remainingDays: number;
  maxExpireDate: Date;
  freeSubscription: Boolean;
  hasActivePartnerSubscriptions: boolean;

  constructor(
    @Inject(WINDOW) private window: any,
    @Inject(LOCAL_STORAGE) private storage: Storage,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store,
    private snackbar: MatSnackBar
  ) {
    super();
    this.applyForMembershipClicked = new EventEmitter();
    this.membershipButtonClicked = new EventEmitter();
  }

  ngOnInit(): void {
    this.getMembershipStatus();
    this.getMembershipButtonClickedProperty();
    this.hasActiveFreeSubscriptionStatus$.pipe(
      takeUntil(this.subscription$)
    ).subscribe((freeSubscription: Boolean) => {
      this.freeSubscription = freeSubscription;
    });
  }

  ngAfterViewInit(): void {

    this.partner$.pipe(
      filter(e => !!e),
      take(1)
    ).subscribe((partner: Partner) => {
      this.store.dispatch(new GetPartnerActiveMembershipSubscriptionsByPartnerId(partner.id!));
      this.activePartnerSubscriptions$.pipe(
        take(1)
      ).subscribe((partnerSubscriptions: PartnerMembershipSubscription[]) => {
        this.hasActivePartnerSubscriptions = partnerSubscriptions.length > 0;
      });
    });
  }

  ngOnDestroy(): void {
    this.destroySubscription();
  }

  getMembershipButtonClick(): void {

    const currentUrl = (this.route as any)['_routerState'].snapshot.url;
    localStorage.setItem('membershipRedirectUrl', currentUrl);

    this.userAuthState$
      .pipe(take(1))
      .subscribe((user: User) => {
        if (user) {
          this.partner$
            .pipe(take(1))
            .subscribe((partner: Partner) => {
              if (partner) {
                this.openMembershipDetailsModal(partner, user);
              }
            });
        } else {
          this.store.dispatch(new OpenMembershipDetailsModal());
          this.login();
        }

        this.membershipButtonClicked.emit();
      });
  }

  async login(): Promise<void> {
    try {
      // this.httpAuthService.warmUpRequest();
      // redirect the user to authenticate page.
      // authenticate page will handle the authentication part
      // const redirectUrl: string = encodeURIComponent(this.urlService.getFullUrl());
      const redirectUrl: string = encodeURIComponent(window.location.href);
      this.router.navigate(['/auth/authenticate'], { queryParams: { redirectUrl: redirectUrl } });
    } catch (error) {
      console.error(error);
    }
  }

  private getMembershipStatus() {
    this.userAuthState$
      .pipe(
        takeUntil(this.subscription$)
      ).subscribe((user: User) => {
        this.hasMembership = false;
        if (user) {
          this.memberships$.pipe(
            takeUntil(this.subscription$)
          ).subscribe((memberships: Membership[]) => {
            if (memberships != null && memberships.length > 0) {
              const activeMemberships = memberships.filter(f => f.status === MembershipStatus.Active);
              const pendingMemberships = memberships.filter(f => f.status === MembershipStatus.Pending);

              if (activeMemberships.length > 0 || pendingMemberships.length > 0) {
                this.hasMembership = true;

                const setStatus = MembershipStatus[MembershipStatus.Pending];
                let membershipStatus = setStatus;

                if (activeMemberships.length > 0) {
                  membershipStatus = MembershipStatus[MembershipStatus.Active];

                  this.memberships = memberships;
                  const currentDate = new Date();
                  const expiresAt = this.maxExpireDate = new Date(Math.max.apply(null, activeMemberships.map(s => new Date(s.expiresAt)) as any));
                  this.remainingDays = Math.floor((Date.UTC(expiresAt.getFullYear(), expiresAt.getMonth(), expiresAt.getDate()) - Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate())) / (1000 * 60 * 60 * 24));
                }
                this.membershipStatus = membershipStatus;
                this.membershipStatusDisplay = `toolbars.membership${membershipStatus}`;
              }
            }
          });
        }
      });
  }

  openMembershipDetailsModal(partner: Partner, user: User) {

    const subscriptions = this.store.selectSnapshot<PartnerMembershipSubscription[]>((state: any) => state.partnerMembershipSubscription.subscriptions);

    if (subscriptions?.length > 0) {
      return this.router.navigateByUrl('/memberships');
    }    

    // this.dialog.open(ModalMembershipComponent, {
    //   id: 'MembershipModal',
    //   width: '727px',
    //   maxWidth: '95%',
    //   autoFocus: false,
    //   data: {
    //     partnerName: partner.name,
    //     email: user.email,
    //     expiresAt: this.memberships ? this.maxExpireDate : null,
    //     forRenewal: this.remainingDays < 0,
    //     isTicketMembershipsOnly: this.isTicketMembershipsOnly
    //   },
    // });
  }

  private getMembershipButtonClickedProperty() {

    // this.membershipButtonClicked$.subscribe((clicked: boolean) => {
    //   if (clicked === true) {
    //     this.userAuthState$.subscribe((user: User) => {
    //       if (user) {
    //         this.partner$.subscribe((partner: Partner) => {
    //           if (partner) {
    //             this.membershipLoaded$.subscribe((loaded: boolean) => {
    //               if (loaded) {
    //                 this.hasAppliedForFreeMembershipWithNoResponse$
    //                   .pipe(take(1))
    //                   .subscribe((noResponse: boolean) => {
    //                     if (!this.membershipStatus && !noResponse) {
    //                       setTimeout(() => {
    //                         if (!this.dialog.getDialogById('MembershipModal')) {
    //                           this.openMembershipDetailsModal(partner, user);
    //                         }
    //                       }, 2000);
    //                     } else {
    //                       this.store.dispatch(new ResetMembershipButtonClickedProperty());
    //                     }
    //                   });
    //               }
    //             });
    //           }
    //         });
    //       }
    //     });
    //   }
    // });

    const $ = forkJoin({
      clicked: this.membershipButtonClicked$,
      userAuth: this.userAuthState$,
      partner: this.partner$,
      loaded: this.membershipLoaded$,
      hasAppliedForFreeMembershipWithNoResponse: this.hasAppliedForFreeMembershipWithNoResponse$.pipe( take(1) )
    }).pipe(
      filter((response) => 
        response.clicked 
        && response.loaded 
        && !!response.userAuth
        && !!response.partner
      )
    );

    $.pipe(
      filter((response) => !this.membershipStatus && !response.hasAppliedForFreeMembershipWithNoResponse),
      filter(() => !this.dialog.getDialogById('MembershipModal')),
      delay(1000),
      tap((response) => this.openMembershipDetailsModal(response.partner, response.userAuth)),
      takeUntil(this.subscription$)
    ).subscribe();
  }

}
